import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  ZoomOut, 
  Maximize2,
  BookOpen,
  Shield,
  X,
  Smartphone
} from 'lucide-react';

const SecureEbookReader = () => {
  const { ebookId } = useParams();
  const navigate = useNavigate();
  
  const [ebook, setEbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readToken, setReadToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const [fitToWidth, setFitToWidth] = useState(false);

  // Disable right-click
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e) => {
      // Disable Ctrl+S, Ctrl+P, Ctrl+C, F12, PrintScreen
      if (
        (e.ctrlKey && (e.key === 's' || e.key === 'p' || e.key === 'c' || e.key === 'a')) ||
        e.key === 'F12' ||
        e.key === 'PrintScreen'
      ) {
        e.preventDefault();
        return false;
      }
    };

    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    const handlePrint = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);
    window.addEventListener('beforeprint', handlePrint);

    // Add CSS to disable text selection
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    document.body.style.MozUserSelect = 'none';

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      window.removeEventListener('beforeprint', handlePrint);
      
      // Restore text selection
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.MozUserSelect = '';
    };
  }, []);

  // Fetch ebook metadata and get reading token
  useEffect(() => {
    const initializeReader = async () => {
      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('access_token');

        // Get reading session token
        const response = await fetch(`${backendUrl}/api/library/${ebookId}/read`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to initialize reader');
        }

        const data = await response.json();
        setEbook(data.ebook);
        setReadToken(data.read_token);
        setTotalPages(data.total_pages || 1);
        setCurrentPage(1);
      } catch (err) {
        console.error('Reader init error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeReader();
  }, [ebookId]);

  // Load page content
  const loadPage = useCallback(async (pageNum) => {
    if (!readToken || !ebook) return;

    try {
      setLoadingPage(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');

      const response = await fetch(
        `${backendUrl}/api/library/${ebookId}/page/${pageNum}?read_token=${readToken}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load page');
      }

      // Get the page as a blob (image or PDF page rendered as image)
      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      setPageContent(imageUrl);
    } catch (err) {
      console.error('Page load error:', err);
    } finally {
      setLoadingPage(false);
    }
  }, [ebookId, readToken, ebook]);

  useEffect(() => {
    if (readToken && ebook) {
      loadPage(currentPage);
    }
  }, [currentPage, readToken, ebook, loadPage]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      if (pageContent) {
        URL.revokeObjectURL(pageContent);
      }
    };
  }, [pageContent]);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Touch gesture handling for mobile swipe navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNextPage();
    } else if (isRightSwipe) {
      goToPrevPage();
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyNav = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        goToNextPage();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        goToPrevPage();
      }
    };

    document.addEventListener('keydown', handleKeyNav);
    return () => document.removeEventListener('keydown', handleKeyNav);
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading secure reader...</p>
          <p className="text-gray-400 text-sm mt-2">Initializing DRM protection</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/library')}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Return to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-[100] bg-gray-900 flex flex-col"
      style={{ 
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none'
      }}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
    >
      {/* Top Navigation Bar */}
      <div className="bg-gray-800 border-b border-gray-700 px-2 sm:px-4 py-2 sm:py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={() => navigate('/library')}
              className="flex items-center text-gray-300 hover:text-white transition-colors p-1 sm:p-0"
            >
              <ArrowLeft className="w-5 h-5 sm:mr-2" />
              <span className="hidden sm:inline">Back to Library</span>
            </button>
            
            <div className="hidden lg:flex items-center space-x-2 text-gray-400">
              <BookOpen className="w-5 h-5" />
              <span className="text-white font-medium truncate max-w-xs">
                {ebook?.title}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-1 sm:space-x-2">
            {/* Security Badge - hidden on small mobile */}
            <div className="hidden sm:flex items-center space-x-1 bg-green-900/50 text-green-400 px-2 sm:px-3 py-1 rounded-full text-xs">
              <Shield className="w-3 h-3" />
              <span>Protected</span>
            </div>

            {/* Fit to Width Toggle (Mobile) */}
            <button
              onClick={() => {
                setFitToWidth(!fitToWidth);
                if (!fitToWidth) setZoom(100);
              }}
              className={`sm:hidden p-1.5 rounded-lg transition-colors ${
                fitToWidth ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:text-white'
              }`}
              title="Fit to Width"
            >
              <Smartphone className="w-4 h-4" />
            </button>

            {/* Zoom Controls */}
            <div className="flex items-center space-x-0.5 sm:space-x-1 bg-gray-700 rounded-lg p-0.5 sm:p-1">
              <button
                onClick={handleZoomOut}
                disabled={fitToWidth}
                className={`p-1.5 sm:p-2 rounded transition-colors ${
                  fitToWidth ? 'text-gray-500 cursor-not-allowed' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-gray-300 text-xs sm:text-sm px-1 sm:px-2 min-w-[2.5rem] sm:min-w-[3rem] text-center">
                {fitToWidth ? 'Fit' : `${zoom}%`}
              </span>
              <button
                onClick={handleZoomIn}
                disabled={fitToWidth}
                className={`p-1.5 sm:p-2 rounded transition-colors ${
                  fitToWidth ? 'text-gray-500 cursor-not-allowed' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="p-1.5 sm:p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              title="Toggle Fullscreen"
            >
              {isFullscreen ? <X className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Reader Content */}
      <div 
        className="flex-1 min-h-0 flex items-center justify-center p-2 sm:p-4 overflow-auto"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div 
          className={`relative bg-white shadow-2xl rounded-lg overflow-hidden mx-auto ${
            fitToWidth ? 'w-full max-w-full' : 'w-full max-w-4xl'
          }`}
          style={{
            transform: fitToWidth ? 'none' : `scale(${zoom / 100})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s ease',
            maxHeight: fitToWidth ? 'none' : '100%'
          }}
        >
          {loadingPage ? (
            <div className="w-full aspect-[3/4] flex items-center justify-center bg-gray-100">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          ) : pageContent ? (
            <img
              src={pageContent}
              alt={`Page ${currentPage}`}
              className="w-full h-auto pointer-events-none"
              style={{ 
                userSelect: 'none',
                WebkitUserDrag: 'none'
              }}
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : (
            <div className="w-full aspect-[3/4] flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-orange-50 p-4 sm:p-8">
              <BookOpen className="w-16 h-16 sm:w-24 sm:h-24 text-purple-300 mb-4 sm:mb-6" />
              <h3 className="text-lg sm:text-2xl font-bold text-gray-800 mb-2 text-center">
                {ebook?.title}
              </h3>
              <p className="text-gray-600 text-center mb-4 text-sm sm:text-base">
                {ebook?.author}
              </p>
              <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500">
                <Shield className="w-4 h-4 text-green-500" />
                <span>Protected Content - In-App Reading Only</span>
              </div>
              <p className="mt-4 sm:mt-6 text-gray-500 text-xs sm:text-sm text-center">
                Use the navigation controls below to read
              </p>
            </div>
          )}

          {/* Watermark Overlay */}
          <div 
            className="absolute inset-0 pointer-events-none opacity-[0.03] flex items-center justify-center overflow-hidden"
            style={{ zIndex: 10 }}
          >
            <div 
              className="text-gray-900 text-2xl font-bold transform rotate-[-30deg] whitespace-nowrap"
              style={{ letterSpacing: '0.5em' }}
            >
              OBED ANSU ACADEMY • PROTECTED CONTENT • OBED ANSU ACADEMY • PROTECTED CONTENT
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="bg-gray-800 border-t border-gray-700 px-2 sm:px-4 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          {/* Previous Button */}
          <button
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            className={`flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 sm:py-3 rounded-lg transition-colors min-w-[44px] ${
              currentPage <= 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800'
            }`}
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="hidden sm:inline">Previous</span>
          </button>

          {/* Page Indicator */}
          <div className="flex flex-col sm:flex-row items-center sm:space-x-4">
            <span className="text-gray-300 text-sm sm:text-base">
              <span className="font-bold text-white">{currentPage}</span>
              <span className="text-gray-400"> / </span>
              <span className="font-bold text-white">{totalPages}</span>
            </span>
            
            {/* Page Input - visible on larger screens */}
            <div className="hidden md:flex items-center space-x-2">
              <span className="text-gray-400 text-sm">Go to:</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= totalPages) {
                    setCurrentPage(page);
                  }
                }}
                className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-center text-sm focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          {/* Next Button */}
          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            className={`flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 sm:py-3 rounded-lg transition-colors min-w-[44px] ${
              currentPage >= totalPages
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800'
            }`}
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Title Bar */}
      <div className="md:hidden bg-gray-800 border-t border-gray-700 px-4 py-2">
        <p className="text-white text-sm text-center truncate">{ebook?.title}</p>
      </div>
    </div>
  );
};

export default SecureEbookReader;
